import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeftRight } from 'lucide-react'
import { createStockMovement, getInventoryItems, getStockLevels } from '../../api/inventory'
import ErrorBanner from '../../components/ErrorBanner'
import { useToast } from '../../components/Toast.jsx'
import { BackLink, DocHeader } from '../../components/PageParts'
import { useSession } from '../../session'
import { num } from '../sales/salesShared'
import ItemPicker from './ItemPicker'
import { qty, stockItems, unitCost, useLocations } from './inventoryShared'

// Move stock from one location to another. With bin tracking on, moves
// happen bin to bin on the Bins pages instead.
export default function MoveStock() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const { notify } = useToast()
  const { binTracking } = useSession()
  const locations = useLocations()
  const [items, setItems] = useState([])
  const [productId, setProductId] = useState(params.get('product_id') || '')
  const [levels, setLevels] = useState([])
  const [count, setCount] = useState('')
  const [fromId, setFromId] = useState('')
  const [toId, setToId] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { getInventoryItems().then((d) => setItems(stockItems(d))).catch(() => setItems([])) }, [])

  const item = items.find((p) => String(p.product_id) === String(productId))

  // Where the item is now; start from the location holding the most.
  useEffect(() => {
    setLevels([])
    if (!productId) return
    getStockLevels(productId).then((lv) => {
      const rows = Array.isArray(lv) ? lv : []
      setLevels(rows)
      const most = [...rows].sort((a, b) => num(b.quantity) - num(a.quantity))[0]
      if (most) {
        setFromId(String(most.location_id))
        setToId((t) => (t && t !== String(most.location_id) ? t : String(rows.find((r) => r.location_id !== most.location_id)?.location_id || '')))
      }
    }).catch(() => setLevels([]))
  }, [productId])

  const atFrom = num(levels.find((l) => String(l.location_id) === String(fromId))?.quantity)
  const nameOf = (id) => locations?.find((l) => String(l.location_id) === String(id))?.name || ''

  const save = async () => {
    setError('')
    const n = num(count)
    if (!item) { setError('Choose the product.'); return }
    if (!(n > 0)) { setError('Enter how many to move.'); return }
    if (!fromId || !toId) { setError('Choose where it’s moving from and to.'); return }
    if (fromId === toId) { setError('Choose two different locations.'); return }
    if (n > atFrom + 1e-9) { setError(`Only ${qty(atFrom)} are at ${nameOf(fromId)}.`); return }
    setSaving(true)
    try {
      await createStockMovement({
        product_id: item.product_id,
        quantity: n,
        move_type: 'transfer',
        from_location_id: Number(fromId),
        to_location_id: Number(toId),
        unit_cost: unitCost(item),
        notes: notes.trim() || undefined,
      })
      notify(`Moved ${qty(n)} ${item.name || item.sku} to ${nameOf(toId)}`)
      nav(`/inventory/stock/${item.product_id}`)
    } catch (e) {
      setError(e.message || 'Could not move the stock.')
    } finally {
      setSaving(false)
    }
  }

  if (binTracking) return <Navigate to="/inventory/bin-move-sku" replace />

  if (locations && locations.length < 2) {
    return (
      <div className="ci-form-page">
        <DocHeader back={<BackLink to="/inventory/stock" label="Stock" />} title="Move stock" />
        <section className="doc-card doc-done">
          <ArrowLeftRight size={28} aria-hidden="true" />
          <div>
            <h2>You have one location</h2>
            <p>Moving stock needs at least two. Add another shop or store room under Master Files.</p>
          </div>
          <div className="doc-done-actions"><Link className="ci-btn ci-btn-primary" to="/master/locations">Locations</Link></div>
        </section>
      </div>
    )
  }

  return (
    <div className="ci-form-page">
      <DocHeader back={<BackLink to="/inventory/stock" label="Stock" />} title="Move stock" meta="Send stock from one location to another. The total doesn’t change." />

      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card">
        <ItemPicker items={items} value={productId} onChange={setProductId} />
        {item && levels.length > 0 && (
          <p className="doc-hint">{levels.map((l) => `${l.location_name}: ${qty(l.quantity)}`).join(' · ')}</p>
        )}
      </div>

      <div className="ci-form-meta-card">
        <div className="ci-form-meta-top three">
          <label className="ci-form-field">
            <span>From</span>
            <select value={fromId} onChange={(e) => setFromId(e.target.value)}>
              <option value="">Choose…</option>
              {(locations || []).map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
            </select>
          </label>
          <label className="ci-form-field">
            <span>To</span>
            <select value={toId} onChange={(e) => setToId(e.target.value)}>
              <option value="">Choose…</option>
              {(locations || []).filter((l) => String(l.location_id) !== String(fromId)).map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
            </select>
          </label>
          <label className="ci-form-field">
            <span>How many{fromId && item ? ` (${qty(atFrom)} there)` : ''}</span>
            <input value={count} onChange={(e) => setCount(e.target.value)} inputMode="decimal" placeholder="0" />
          </label>
        </div>
      </div>

      <label className="ci-form-field ci-form-note">
        <span>Note (optional)</span>
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Restocking the front shop" />
      </label>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving || !item} onClick={save}><ArrowLeftRight size={14} />{saving ? 'Moving…' : 'Move stock'}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav(-1)}>Cancel</button>
        </div>
      </div>

    </div>
  )
}
